<script lang="ts">
	import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
	import { format, locale } from 'svelte-i18n';
	import { dateFormat } from 'src/helpers/date_helper';
	import { CHANGE_DATE } from 'src/constants';
	import { localizedLastUpdate } from 'src/helpers/localized_last_update_helper';
	import AISceneModalWrapper from './AISceneModalWrapper.svelte';

	$: lastUpdate = localizedLastUpdate(
		CHANGE_DATE.AI_PP * 1000,
		$locale,
		'ai_privacy_policy_last_update',
		$format,
	);

	function handleClose() {
		aiSceneChatStore.closePrivacyPolicy();
	}
</script>

<AISceneModalWrapper
	open={$aiSceneChatStore.showPrivacyPolicyModal}
	onClose={handleClose}
	bordered
	size="content"
>
	<div class="modal-content">
		<div class="header">
			<h1 class="modal-title">
				{$format('ai_privacy_policy_title', { default: 'Privacy policy' })}
			</h1>
			{#if lastUpdate}
				<p class="modal-subtitle">
					{lastUpdate}
				</p>
			{/if}
			<div class="divider"></div>
		</div>

		<div class="policy-content">
			<!-- AI Transparency Notice -->
			<div class="notice-box">
				<h3 class="notice-title">
					{$format('ai_tou_notice_title', {
						default: 'AI TRANSPARENCY NOTICE — PLEASE READ BEFORE USING SHELLY AI ASSISTANT',
					})}
				</h3>
				<p class="policy-text">
					{$format('ai_tou_notice_p1', {
						default:
							'Shelly AI Assistant is an AI-powered feature. When you interact with Shelly AI Assistant, you are communicating with an artificial intelligence system, not a human.',
					})}
				</p>
				<p class="policy-text">
					{$format('ai_tou_notice_p2', {
						default:
							'Outputs generated by Shelly AI Assistant — including automation suggestions, device configurations, and scenes — are produced by an AI agent. Its suggestions do not constitute professional electrical, technical, safety, or energy advice and have not been individually reviewed or verified by Shelly Europe Ltd, its staff, or licensed Shelly Installer.',
					})}
				</p>
				<p class="policy-text">
					{$format('ai_tou_notice_p3', {
						default:
							'Always review AI suggestions carefully before applying them to your devices. Shelly AI Assistant is an assistive tool only. Shelly AI Assistant does not autonomously apply changes to your devices. All suggestions made by Shelly AI Assistant must be reviewed and actively confirmed by you before they are applied. You retain full control over your home and devices at all times.',
					})}
				</p>
				<p class="policy-text">
					{$format('ai_tou_notice_p4', {
						default:
							'Shelly AI Assistant must not be used in life-safety, emergency, medical, or other safety-critical systems and infrastructures. It must not be relied upon as the sole mechanism for fire detection, intrusion protection, health monitoring, emergency shut-off, or similar critical functions.',
					})}
				</p>
			</div>

			<h2 class="section-title">
				{$format('ai_tou_s1_title', { default: '1. INTRODUCTION AND SCOPE' })}
			</h2>
			<p class="policy-text">
				{$format('ai_tou_s1_p1', {
					default:
						'1.1 These Shelly AI Assistant Terms of Use ("Terms") govern your access to and use of the Shelly AI Assistant feature ("Shelly AI Assistant" or "the Feature") and any of its current and future models and their versions, which is made available within the Shelly Smart Control mobile application ("the App") and its web-based version, referred hereinafter as the "AI Service".',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s1_p2', {
					default:
						'1.2 These Terms are a standalone agreement between you ("you" or "the User") and Shelly Europe Ltd., registered at 1407 Sofia, Bulgaria, 51 Cherni Vrah Blvd., building 3, office 2 and 3, Bulgaria ("Shelly", "we", "us"). They operate independently of and in addition to Shelly Smart Control\'s General Terms and Conditions, which continue to apply to your use of the AI Service.',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s1_p3', {
					default:
						'1.3 If there is any conflict between these Terms and the Shelly Smart Control General Terms and Conditions on a matter specifically related to Shelly AI Assistant, these Terms take precedence for that matter.',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s1_p4', {
					default:
						'1.4 These Terms apply to consumers using Shelly AI Assistant for private, non-commercial purposes. If you are using Shelly AI Assistant in a business or professional capacity, different terms may apply — please contact us at support@shelly.cloud',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s1_p5', {
					default:
						'1.5 By clicking "Start using Shelly AI Assistant" or by otherwise accessing or using Shelly AI Assistant, you confirm that you have read and understood these Terms and agree to be bound by them. If you do not agree, please do not use Shelly AI Assistant.',
				})}
			</p>

			<h2 class="section-title">
				{$format('ai_tou_s2_title', { default: '2. WHAT IS SHELLY AI ASSISTANT?' })}
			</h2>
			<p class="policy-text">
				{$format('ai_tou_s2_p1', {
					default:
						'2.1 Shelly AI Assistant is an AI-powered conversational feature integrated into the Shelly Smart Control Service. It uses artificial intelligence and machine learning models to help you configure, automate, and manage your Shelly smart home devices.',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s2_p2', {
					default: '2.2 Shelly AI Assistant can assist you with tasks including:',
				})}
			</p>
			<ul class="policy-list">
				<li>
					{$format('ai_tou_s2_li1', {
						default:
							'generating automation suggestions and schedules for your Shelly devices;',
					})}
				</li>
				<li>
					{$format('ai_tou_s2_li2', {
						default: 'proposing device configuration settings and parameters;',
					})}
				</li>
				<li>
					{$format('ai_tou_s2_li3', {
						default: 'creating and suggesting scenes (grouped device actions);',
					})}
				</li>
				<li>
					{$format('ai_tou_s2_li4', {
						default: 'answering questions about Shelly device functionality and settings;',
					})}
				</li>
				<li>
					{$format('ai_tou_s2_li5', {
						default:
							'providing general guidance on how to get more from your smart home setup.',
					})}
				</li>
			</ul>
			<p class="policy-text">
				{$format('ai_tou_s2_p3', {
					default:
						'2.3 Shelly AI Assistant does not autonomously apply changes to your devices. All suggestions made by Shelly AI Assistant must be reviewed and actively confirmed by you before they are applied. You retain full control over your devices at all times.',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s2_p4', {
					default:
						'2.4 Shelly AI Assistant may, in part, be powered by third-party AI model providers. Shelly selects and oversees these providers but cannot guarantee the accuracy, completeness, or fitness for purpose of AI-generated outputs.',
				})}
			</p>

			<h2 class="section-title">
				{$format('ai_tou_s3_title', { default: '3. ACCESS TO SHELLY AI ASSISTANT' })}
			</h2>
			<p class="policy-text">
				{$format('ai_tou_s3_p1', {
					default:
						'3.1 Access to Shelly AI Assistant may depend on your service tier within Shelly Smart Control.',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s3_p2', {
					default:
						'3.2 Standard Service Users are provided with access to up to 10 prompts per day.',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s3_p3', {
					default:
						'3.3 Premium Service Users, which is a pre-paid subscription-based access to upgraded features of Shelly Smart Control, are provided with access to up to 50 prompts per day within the applicable usage period.',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s3_p4', {
					default:
						'3.4 A "prompt" means a request submitted to Shelly AI Assistant that generates an AI-based Output. Prompt limits apply per user account and may reset in accordance with the applicable subscription or billing cycle. For further details regarding Standard Service, Premium Service, subscription terms, billing, renewal, and cancellation conditions, please refer to the Shelly Smart Control General Terms and Conditions available at: LINK',
				})}
			</p>

			<h2 class="section-title">
				{$format('ai_tou_s4_title', {
					default: '4. CAPABILITIES AND LIMITATIONS OF SHELLY AI ASSISTANT',
				})}
			</h2>
			<p class="policy-text">
				{$format('ai_tou_s4_p1', {
					default:
						'4.1 Shelly AI Assistant has the following known limitations, which you should understand before relying on any output:',
				})}
			</p>
			<ul class="policy-list">
				<li>
					{$format('ai_tou_s4_li1', {
						default:
							'AI outputs (including recommendation for purchase of devices) may be incorrect, incomplete, outdated, or unsuitable for your specific devices or living environment;',
					})}
				</li>
				<li>
					{$format('ai_tou_s4_li2', {
						default:
							'Shelly AI Assistant does not have real-time knowledge of your device, device status, firmware version, local electrical infrastructure, wiring/load limitations, network connectivity, environmental conditions, third-party integrations or building regulations in your area; Shelly does not guarantee that any AI-generated suggestion, once applied, will work as intended or produce a specific outcome;',
					})}
				</li>
				<li>
					{$format('ai_tou_s4_li3', {
						default:
							'Shelly AI Assistant may not account for the physical limitations of your devices, wiring configurations, load capacities, or safety-critical interoperability requirements;',
					})}
				</li>
				<li>
					{$format('ai_tou_s4_li4', {
						default:
							'AI language models can occasionally produce plausible-sounding but factually incorrect responses ("hallucinations"). Always verify technical details independently;',
					})}
				</li>
				<li>
					{$format('ai_tou_s4_li5', {
						default:
							"The availability and quality of Shelly AI Assistant's responses may vary depending on the AI agent in use, AI agent's version, network conditions, and service availability;",
					})}
				</li>
				<li>
					{$format('ai_tou_s4_li6', {
						default:
							'In certain cases, when you explicitly request Shelly AI Assistant to trigger an immediate action (for example, turning a device on or off), the requested action may be executed directly and are not subject to additional confirmation or prior review. By submitting such a request, you authorize Shelly AI Assistant to transmit the command to the relevant device.',
					})}
				</li>
			</ul>
			<p class="policy-text">
				{$format('ai_tou_s4_p2', {
					default:
						'4.2 Shelly AI Assistant is designed to assist and guide. It is not dedicated to guide or replace:',
				})}
			</p>
			<ul class="policy-list">
				<li>
					{$format('ai_tou_s4_li7', {
						default:
							'any action requiring qualified electricians or electrical engineers input;',
					})}
				</li>
				<li>
					{$format('ai_tou_s4_li8', {
						default: 'professional smart home installation services;',
					})}
				</li>
				<li>
					{$format('ai_tou_s4_li9', {
						default: 'safety assessments or risk evaluations;',
					})}
				</li>
				<li>
					{$format('ai_tou_s4_li10', {
						default:
							'advice on compliance with local electrical codes, building regulations, or insurance requirements.',
					})}
				</li>
			</ul>
			<p class="policy-text">
				{$format('ai_tou_s4_p3', {
					default:
						'4.3 Shelly AI Assistant is provided as an experimental feature. Its outputs should be treated as starting-point suggestions that require your informed judgment before application.',
				})}
			</p>

			<h2 class="section-title">
				{$format('ai_tou_s5_title', { default: '5. YOUR RESPONSIBILITIES' })}
			</h2>
			<p class="policy-text">
				{$format('ai_tou_s5_p1', {
					default: '5.1 By using Shelly AI Assistant, you acknowledge and agree that:',
				})}
			</p>
			<ul class="policy-list">
				<li>
					{$format('ai_tou_s5_li1', {
						default:
							'You are responsible for reviewing all AI-generated suggestions before applying them to any device, automation, scene, or configuration.',
					})}
				</li>
				<li>
					{$format('ai_tou_s5_li2', {
						default:
							'You must not apply any AI-generated configuration that you do not fully understand or that may pose a safety risk to yourself, others, or your property.',
					})}
				</li>
				<li>
					{$format('ai_tou_s5_li3', {
						default:
							'You bear sole responsibility for any consequences resulting from your decision to implement a suggestion made by Shelly AI Assistant.',
					})}
				</li>
				<li>
					{$format('ai_tou_s5_li4', {
						default:
							'Where a configuration relates to electrical loads, heating systems, or other safety-critical infrastructure, you are strongly advised to consult a qualified professional before implementation.',
					})}
				</li>
				<li>
					{$format('ai_tou_s5_li5', {
						default:
							'You shall comply with all applicable laws, regulations, and standards when configuring and operating your Shelly devices.',
					})}
				</li>
			</ul>
			<p class="policy-text">
				{$format('ai_tou_s5_p2', {
					default: '5.2 Shelly strongly recommends that users:',
				})}
			</p>
			<ul class="policy-list">
				<li>
					{$format('ai_tou_s5_li6', {
						default:
							'test new automations or configurations in non-critical environments before wide deployment;',
					})}
				</li>
				<li>
					{$format('ai_tou_s5_li7', {
						default: 'monitor device behavior after applying new configurations;',
					})}
				</li>
				<li>
					{$format('ai_tou_s5_li8', {
						default:
							'consult the Shelly documentation and, where appropriate, a qualified technician for complex setups.',
					})}
				</li>
			</ul>

			<h2 class="section-title">
				{$format('ai_tou_s6_title', { default: '6. ACCEPTABLE USE' })}
			</h2>
			<p class="policy-text">
				{$format('ai_tou_s6_p1', {
					default:
						'6.1 You may use Shelly AI Assistant only for lawful purposes and in accordance with these Terms. You agree not to use Shelly AI Assistant to:',
				})}
			</p>
			<ul class="policy-list">
				<li>
					{$format('ai_tou_s6_li1', {
						default:
							'attempt to extract, reverse-engineer, or replicate the underlying AI models or system prompts;',
					})}
				</li>
				<li>
					{$format('ai_tou_s6_li2', {
						default:
							'input data that violates the privacy, intellectual property, or other rights of third parties;',
					})}
				</li>
				<li>
					{$format('ai_tou_s6_li3', {
						default:
							'generate content that is misleading, fraudulent, abusive, or otherwise unlawful;',
					})}
				</li>
				<li>
					{$format('ai_tou_s6_li4', {
						default:
							'deliberately overload, disrupt, or interfere with the Shelly AI Assistant service or underlying infrastructure;',
					})}
				</li>
				<li>
					{$format('ai_tou_s6_li5', {
						default:
							'use Shelly AI Assistant for any commercial resale or redistribution of AI outputs without our prior written consent;',
					})}
				</li>
				<li>
					{$format('ai_tou_s6_li6', {
						default:
							'circumvent or attempt to bypass any safety or content filtering mechanisms built into the Feature;',
					})}
				</li>
				<li>
					{$format('ai_tou_s6_li7', {
						default:
							'control life-safety, emergency, medical, or other safety-critical systems and infrastructures.',
					})}
				</li>
			</ul>
			<p class="policy-text">
				{$format('ai_tou_s6_p2', {
					default:
						'6.2 We reserve the right to monitor the use of Shelly AI Assistant for the purposes of safety, quality assurance, service improvement, and compliance with these Terms.',
				})}
			</p>

			<h2 class="section-title">
				{$format('ai_tou_s7_title', { default: '7. DATA PROCESSING' })}
			</h2>
			<p class="policy-text">
				{$format('ai_tou_s7_p1', {
					default:
						'7.1 Shelly AI Assistant focuses on providing AI-generated suggestions and guidance on Shelly devices and Shelly services. To provide this support, it only uses essential device and account information, and does not require any additional data. The use of Shelly AI Assistant as intended does not involve the processing of personal data. Any conversational inputs you submit to Shelly AI Assistant, device usage context and configuration data, and technical information necessary to deliver and improve the feature are processed on anonymized basis.',
				})}
			</p>
			<p class="policy-text notice-warning">
				{$format('ai_tou_s7_p2', {
					default:
						'NB! Do not use Shelly AI Assistant for purposes other than those for its intended purpose.',
				})}
			</p>
			<p class="policy-text notice-warning">
				{$format('ai_tou_s7_p3', {
					default:
						'NB! Do not provide Shelly AI Assistant with personal data such data might be processed by third-party AI providers where Shelly AI Assistant is powered by such. We take care to select providers that offer adequate data protection standards compatible with EU law. However, by submitting your personal data other than device and account data, you agree with any further processing by third-party AI providers.',
				})}
			</p>

			<h2 class="section-title">
				{$format('ai_tou_s8_title', { default: '8. DISCLAIMER OF WARRANTIES' })}
			</h2>
			<p class="policy-text">
				{$format('ai_tou_s8_p1', {
					default:
						'8.1 Shelly AI Assistant is provided "as available" and "as is". To the maximum extent permitted by applicable law, Shelly makes no representations or warranties, express or implied, regarding:',
				})}
			</p>
			<ul class="policy-list">
				<li>
					{$format('ai_tou_s8_li1', {
						default:
							'the accuracy, completeness, or suitability of AI-generated outputs for any particular purpose;',
					})}
				</li>
				<li>
					{$format('ai_tou_s8_li2', {
						default: 'the uninterrupted or error-free availability of the Feature;',
					})}
				</li>
				<li>
					{$format('ai_tou_s8_li3', {
						default:
							'the safety or appropriateness of any device configuration suggested by Shelly AI Assistant;',
					})}
				</li>
				<li>
					{$format('ai_tou_s8_li4', {
						default:
							'any energy savings, cost reductions, or performance improvements that may be implied by or result from using AI suggestions;',
					})}
				</li>
				<li>
					{$format('ai_tou_s8_li5', {
						default: 'the suitability and workability of any AI-generated suggestion;',
					})}
				</li>
				<li>
					{$format('ai_tou_s8_li6', {
						default:
							'intended or produce specific outcome of any interaction with the AI Assistant;',
					})}
				</li>
			</ul>
			<p class="policy-text">
				{$format('ai_tou_s8_p2', {
					default:
						'8.2 Nothing in these Terms excludes or limits any statutory rights you have as a consumer under EU or applicable national law that cannot be lawfully excluded or restricted.',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s8_p3', {
					default:
						'8.3 In particular, where the applicable law confers mandatory guarantees or rights of the consumers, those rights are unaffected by these Terms.',
				})}
			</p>

			<h2 class="section-title">
				{$format('ai_tou_s9_title', { default: '9. LIMITATION OF LIABILITY' })}
			</h2>
			<p class="policy-text">
				{$format('ai_tou_s9_p1', {
					default:
						"9.1 Shelly's total aggregate liability to you in connection with Shelly AI Assistant, whether arising in contract, tort (including negligence), or otherwise, shall not exceed the amount you have paid to Shelly for access to the Feature in the twelve (12) months preceding the relevant claim.",
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s9_p2', { default: '9.2 Shelly shall not be liable for:' })}
			</p>
			<ul class="policy-list">
				<li>
					{$format('ai_tou_s9_li1', {
						default:
							'any loss or damage arising from your decision to apply an AI-generated suggestion to a physical device or environment or any improper use of the AI Assistant not compliant with these Terms and Conditions;',
					})}
				</li>
				<li>
					{$format('ai_tou_s9_li2', {
						default:
							'AI-generated automation suggestion – including but not limited to device configurations and scenes;',
					})}
				</li>
				<li>
					{$format('ai_tou_s9_li3', {
						default:
							'plausible-sounding but factually incorrect responses ("hallucinations") of the AI Assistant;',
					})}
				</li>
				<li>
					{$format('ai_tou_s9_li4', {
						default:
							'indirect, consequential, or incidental losses, including loss of data, loss of profit, loss of business, or property damage, to the extent permitted by law;',
					})}
				</li>
				<li>
					{$format('ai_tou_s9_li5', {
						default:
							'interruptions to the Feature caused by factors outside our reasonable control, including internet connectivity, third-party service outages, or force majeure events;',
					})}
				</li>
			</ul>

			<h2 class="section-title">
				{$format('ai_tou_s10_title', { default: '10. SUSPENSION AND TERMINATION' })}
			</h2>
			<p class="policy-text">
				{$format('ai_tou_s10_p1', {
					default:
						'10.1 You may stop using Shelly AI Assistant at any time by simply discontinuing use of the Feature. Ceasing to use Shelly AI Assistant does not affect your access to other features of the Shelly Smart Control App nor to any applicable pre-paid subscription plans.',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s10_p2', {
					default:
						'10.2 Shelly may suspend or restrict your access to Shelly AI Assistant, or discontinue the Feature entirely, if:',
				})}
			</p>
			<ul class="policy-list">
				<li>
					{$format('ai_tou_s10_li1', {
						default:
							'you breach these Terms in a material way and fail to remedy the breach within a reasonable period following notice;',
					})}
				</li>
				<li>
					{$format('ai_tou_s10_li2', {
						default: 'we are required to do so to comply with a legal obligation;',
					})}
				</li>
				<li>
					{$format('ai_tou_s10_li3', {
						default:
							'continued provision of the Feature poses a safety, legal, or technical risk;',
					})}
				</li>
			</ul>
			<p class="policy-text">
				{$format('ai_tou_s10_p3', {
					default:
						'10.3 Where Shelly decides to discontinue Shelly AI Assistant permanently, we will provide reasonable advance notice through the App or by email to the address associated with your account, except where this is not possible due to legal or safety reasons.',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s10_p4', {
					default:
						'10.4 In the event of termination, provisions of these Terms that by their nature are intended to survive (including Clauses 8, 9 and 12) shall continue to apply.',
				})}
			</p>

			<h2 class="section-title">
				{$format('ai_tou_s11_title', { default: '11. THIRD-PARTY AI PROVIDERS' })}
			</h2>
			<p class="policy-text">
				{$format('ai_tou_s11_p1', {
					default:
						'11.1 Shelly AI Assistant may be powered in whole or in part by third-party AI model providers. The identity of these providers may change over time as we update and improve the Feature. We ensure that our arrangements with third-party providers comply with applicable law.',
				})}
			</p>

			<h2 class="section-title">
				{$format('ai_tou_s12_title', { default: '12. AMENDMENTS TO THESE TERMS' })}
			</h2>
			<p class="policy-text">
				{$format('ai_tou_s12_p1', {
					default:
						'12.1 We may update these Terms from time to time to reflect changes to the Feature, applicable law, or our business practices. When we make material changes, we will notify you through the App or via email with reasonable advance notice.',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s12_p2', {
					default:
						'12.2 Your continued use of Shelly AI Assistant after the effective date of amended Terms constitutes your acceptance of the changes. If you do not accept the changes, you may discontinue use of Shelly AI Assistant before the effective date.',
				})}
			</p>

			<h2 class="section-title">
				{$format('ai_tou_s13_title', {
					default: '13. GOVERNING LAW AND DISPUTE RESOLUTION',
				})}
			</h2>
			<p class="policy-text">
				{$format('ai_tou_s13_p1', {
					default:
						"13.1 These Terms are governed by the law of the Republic of Bulgaria, consistent with Shelly's registered place of business.",
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s13_p2', {
					default:
						'13.2 Notwithstanding Clause 13.1, if you are a consumer in the sense of Directive (2011/83/EU) habitually resident in another EU Member State, you retain the benefit of any mandatory consumer protection provisions of the law of your country of residence that cannot be derogated from by agreement. In such cases, those mandatory provisions shall apply in addition to, and prevail over, any conflicting provision of Bulgarian law.',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s13_p3', {
					default:
						'13.3 We hope to resolve any concerns informally. If you have a complaint about Shelly AI Assistant, please contact us first at support@shelly.cloud. We will endeavor to respond within 14 calendar days.',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s13_p4', {
					default:
						'13.4 If a dispute cannot be resolved through informal channels, you may have the right to bring a claim before the competent courts of your EU Member State of residence.',
				})}
			</p>

			<h2 class="section-title">
				{$format('ai_tou_s14_title', { default: '14. CONTACT AND FEEDBACK' })}
			</h2>
			<p class="policy-text">
				{$format('ai_tou_s14_p1', {
					default:
						'14.1 If you have questions about these Terms, Shelly AI Assistant, or your rights as a consumer, please contact us:',
				})}
			</p>
			<p class="policy-text contact-info">
				{@html $format('ai_tou_s14_contact', {
					default:
						'SHELLY EUROPE LTD<br />Sofia, Bulgaria, blvd. Cherni Vrah 51, fl. 2-3, building 3<br />Email: support@shelly.cloud',
				})}
			</p>
			<p class="policy-text">
				{$format('ai_tou_s14_p2', {
					default:
						'14.2 We value your feedback on Shelly AI Assistant. If you encounter unexpected behaviour, incorrect suggestions, or safety concerns, please report them through the in-app feedback mechanism or by contacting support.',
				})}
			</p>

			<!-- Quick Summary -->
			<div class="summary-box">
				<h3 class="summary-title">
					{$format('ai_tou_summary_title', { default: 'Quick Summary' })}
				</h3>
				<ul class="summary-list">
					<li>
						{$format('ai_tou_summary_li1', {
							default: 'Shelly AI Assistant is an AI feature — not a human expert.',
						})}
					</li>
					<li>
						{$format('ai_tou_summary_li2', {
							default:
								'All suggestions are AI-generated and have not been individually reviewed.',
						})}
					</li>
					<li>
						{$format('ai_tou_summary_li3', {
							default:
								'You must review all suggestions before applying them to your devices.',
						})}
					</li>
					<li>
						{$format('ai_tou_summary_li4', {
							default:
								'AI suggestions are not professional electrical, technical, or safety advice.',
						})}
					</li>
					<li>
						{$format('ai_tou_summary_li5', {
							default:
								'Shelly AI Assistant does not apply changes without your confirmation.',
						})}
					</li>
					<li>
						{$format('ai_tou_summary_li6', {
							default: 'Your consumer rights under EU law are fully preserved.',
						})}
					</li>
				</ul>
				<p class="policy-text summary-note">
					{$format('ai_tou_summary_note', {
						default: 'These Terms do not affect your statutory rights as a consumer.',
					})}
				</p>
				<p class="policy-text summary-footer">
					{$format('ai_tou_summary_footer', {
						default: 'SHELLY EUROPE LTD — All rights reserved',
					})}
				</p>
			</div>
		</div>
	</div>
</AISceneModalWrapper>

<style>
	.modal-content {
		display: flex;
		flex-direction: column;
		flex: 1;
		padding: 0 32px;
		position: relative;
		z-index: 1;
		overflow: hidden;
	}

	.header {
		flex-shrink: 0;
	}

	.modal-title {
		font-family: Inter, sans-serif;
		font-size: 22px;
		font-weight: 600;
		line-height: 27px;
		color: var(--color-text-primary, #eaeaea);
		margin: 0 0 8px 0;
	}

	.modal-subtitle {
		font-family: Inter, sans-serif;
		font-size: 13px;
		font-weight: 400;
		line-height: 16px;
		color: rgba(255, 255, 255, 0.7);
		margin: 0;
	}

	.divider {
		width: 100%;
		height: 1px;
		background: rgba(255, 255, 255, 0.7);
		margin: 20px 0 22px 0;
	}

	.policy-content {
		flex: 1;
		overflow-y: auto;
		padding-right: 8px;
	}

	.section-title {
		font-family: Inter, sans-serif;
		font-size: 14px;
		font-weight: 600;
		line-height: 17px;
		color: var(--color-text-primary, #eaeaea);
		margin: 24px 0 12px 0;
	}

	.section-title:first-child {
		margin-top: 0;
	}

	.policy-text {
		text-align: justify;
		font-family: Inter, sans-serif;
		font-size: 14px;
		font-weight: 400;
		line-height: 20px;
		color: rgba(255, 255, 255, 0.7);
		margin: 0 0 16px 0;
	}

	.notice-box {
		background: rgba(191, 227, 243, 0.1);
		border: 1px solid rgba(191, 227, 243, 0.3);
		border-radius: 8px;
		padding: 16px;
		margin-bottom: 24px;
	}

	.notice-title {
		font-family: Inter, sans-serif;
		font-size: 13px;
		font-weight: 600;
		line-height: 16px;
		color: #bfe3f3;
		margin: 0 0 12px 0;
	}

	.notice-warning {
		font-weight: 500;
		color: rgba(255, 200, 100, 0.9);
	}

	.policy-list {
		font-family: Inter, sans-serif;
		color: rgba(255, 255, 255, 0.7);
		font-size: 14px;
		line-height: 20px;
		margin: 0 0 16px 0;
		padding-left: 24px;
	}

	.policy-list li {
		margin-bottom: 8px;
	}

	.policy-list li:last-child {
		margin-bottom: 0;
	}

	.contact-info {
		text-align: left;
	}

	.summary-box {
		background: rgba(255, 255, 255, 0.05);
		border-radius: 8px;
		padding: 16px;
		margin-top: 24px;
	}

	.summary-title {
		font-family: Inter, sans-serif;
		font-size: 14px;
		font-weight: 600;
		line-height: 17px;
		color: var(--color-text-primary, #eaeaea);
		margin: 0 0 12px 0;
	}

	.summary-list {
		font-family: Inter, sans-serif;
		color: rgba(255, 255, 255, 0.7);
		font-size: 14px;
		line-height: 20px;
		margin: 0 0 16px 0;
		padding-left: 20px;
	}

	.summary-list li {
		margin-bottom: 6px;
	}

	.summary-list li:last-child {
		margin-bottom: 0;
	}

	.summary-note {
		font-style: italic;
		margin-bottom: 8px;
	}

	.summary-footer {
		font-size: 12px;
		text-align: center;
		margin-bottom: 0;
		opacity: 0.7;
	}

	@media (max-width: 768px) {
		.modal-content {
			padding: 0 24px;
		}

		.header {
			padding-top: 0;
		}
	}
</style>
